import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  PriceListRequest,
  PriceListResponse,
  PriceListSearchParams,
  PriceListItemRequest,
  PriceListItemResponse,
} from '../models/price-list.model';
// Inc 28: el timeline reutiliza el shape de auditoría y la página estilo audit
// ({ content, page, size, totalElements, totalPages, hasNext, hasPrevious }).
import {
  AuditLogResponse,
  PageResponse as TimelinePageResponse,
} from '../../audit/models/audit.model';

@Injectable({
  providedIn: 'root',
})
export class PriceListApiService {
  private readonly apiUrl = `${environment.apiUrl}api/product-price-lists`;

  constructor(private http: HttpClient) {}

  searchPriceList(
    params: PriceListSearchParams,
  ): Observable<PriceListResponse[]> {
    let httpParams = new HttpParams().set(
      'businessId',
      params.businessId.toString(),
    );
    if (params.isDefault !== undefined)
      httpParams = httpParams.set('isDefault', params.isDefault.toString());
    if (params.currencyId !== undefined)
      httpParams = httpParams.set('currencyId', params.currencyId.toString());
    if (params.searchName)
      httpParams = httpParams.set('searchName', params.searchName);
    if (params.activeOnly !== undefined)
      httpParams = httpParams.set('activeOnly', params.activeOnly.toString());
    return this.http.get<PriceListResponse[]>(this.apiUrl, {
      params: httpParams,
    });
  }

  /**
   * Inc 51: activar/desactivar una lista de precios de forma persistente.
   * PATCH /api/product-price-lists/{id}/active?active=false → ProductPriceListResponse.
   */
  setPriceListActive(
    id: number,
    active: boolean,
    businessId: number,
  ): Observable<PriceListResponse> {
    const params = new HttpParams()
      .set('businessId', businessId.toString())
      .set('active', active.toString());
    return this.http.patch<PriceListResponse>(
      `${this.apiUrl}/${id}/active`,
      null,
      { params },
    );
  }

  getPriceListById(id: number): Observable<PriceListResponse> {
    return this.http.get<PriceListResponse>(`${this.apiUrl}/${id}`);
  }

  createPriceList(req: PriceListRequest): Observable<PriceListResponse> {
    return this.http.post<PriceListResponse>(this.apiUrl, req);
  }

  updatePriceList(
    id: number,
    req: PriceListRequest,
  ): Observable<PriceListResponse> {
    return this.http.put<PriceListResponse>(`${this.apiUrl}/${id}`, req);
  }

  deletePriceList(id: number, businessId: number): Observable<void> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.delete<void>(`${this.apiUrl}/${id}`, { params });
  }

  /**
   * Inc 28: timeline propio de la lista de precios (products:VIEW, ya NO el genérico
   * de auditoría que exigía audit:VIEW y devolvía 403 en silencio). Agrega los
   * sub-eventos de sus ítems (alta/edición/baja de productos de la lista).
   * GET /api/product-price-lists/{id}/timeline
   */
  getPriceListTimeline(
    id: number,
    page: number,
    size: number,
  ): Observable<TimelinePageResponse<AuditLogResponse>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<TimelinePageResponse<AuditLogResponse>>(
      `${this.apiUrl}/${id}/timeline`,
      { params },
    );
  }

  // Items

  getPriceListItems(priceListId: number): Observable<PriceListItemResponse[]> {
    return this.http.get<PriceListItemResponse[]>(
      `${this.apiUrl}/${priceListId}/items`,
    );
  }

  /**
   * Precio resuelto de un producto en una lista de precios (V75).
   * GET /api/product-price-lists/{priceListId}/items/product-price?productId={productId}
   * → 200 con `resolvedPrice` para usar como unitPrice. 404 si la lista no existe o el producto
   * no está en la lista (el llamador debe caer al precio base del producto).
   */
  getProductPrice(
    priceListId: number,
    productId: number,
  ): Observable<PriceListItemResponse> {
    const params = new HttpParams().set('productId', productId.toString());
    return this.http.get<PriceListItemResponse>(
      `${this.apiUrl}/${priceListId}/items/product-price`,
      { params },
    );
  }

  upsertPriceListItem(
    priceListId: number,
    req: PriceListItemRequest,
  ): Observable<PriceListItemResponse> {
    return this.http.post<PriceListItemResponse>(
      `${this.apiUrl}/${priceListId}/items`,
      req,
    );
  }

  deletePriceListItem(
    priceListId: number,
    itemId: number,
    businessId: number,
  ): Observable<void> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.delete<void>(
      `${this.apiUrl}/${priceListId}/items/${itemId}`,
      { params },
    );
  }
}
